import { createSlice } from '@reduxjs/toolkit';
import _, { isArray } from 'lodash';
import moment from 'moment';
import API from '../../app/api';
import Formatter from '../../util/formatter';
import { setSnackbar } from './globals';
import { hasPermission, Permissions, sendUserAudit } from './user';
import { getWardsIdForPatientsSearch } from './wardMap';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { getConfig } from '../../util/configUtil';
import Notes from '../../util/notes';

export const wardSlice = createSlice({
    name: 'ward',
    initialState: {
        wards: [],
        loading: undefined,
        loadingPatients: false,
        loadingWards: false,
        updating: false,
        patients: undefined,
        statuses: undefined,
        bedasList: {},
        singleBedasSessions: {},
        barsList: {},
        singleBarsSessions: {},
        connectionError: false,
        currentMap: null,
        mapHospitalList: [],
        isMultiHospitalMode: false,
        currentHospital: null,
        cleaningRequestState: null,
        cleaningRequestObject: null,
    },
    reducers: {
        setStatuses: (state, statuses) => {
            state.statuses = statuses.payload;
        },
        setWardPatients: (state, wardPatients) => {
            let wardPatientsNew = wardPatients.payload;
            if (wardPatientsNew && isArray(wardPatientsNew)) {
                wardPatientsNew.forEach((wardPatient) => {
                    wardPatient.haveSameName =
                        _.filter(wardPatientsNew, (wp) => {
                            return _.toLower(wardPatient?.patient?.familyName) === _.toLower(wp?.patient?.familyName);
                        }).length > 1;
                });
            }
            state.patients = wardPatientsNew;
        },
        setBedasList: (state, bedas) => {
            state.bedasList = bedas.payload;
        },
        setSingleBedasSessions: (state, bedas) => {
            state.singleBedasSessions = bedas.payload;
        },
        setBarsList: (state, bedas) => {
            state.barsList = bedas.payload;
        },
        setSingleBarsSessions: (state, bedas) => {
            state.singleBarsSessions = bedas.payload;
        },
        setLoading: (state, wardsId) => {
            state.loading = wardsId.payload;
        },
        setUpdating: (state, wardsId) => {
            state.updating = wardsId.payload;
        },
        setConnectionError: (state, isError) => {
            state.connectionError = isError.payload;
        },
        setCurrentMap: (state, ward) => {
            state.currentMap = ward.payload;
        },
        setLoadingPatients: (state, ward) => {
            state.loadingPatients = ward.payload;
        },
        setWards: (state, ward) => {
            state.wards = ward.payload;
        },
        setLoadingWards: (state, ward) => {
            state.loadingWards = ward.payload;
        },
        setMultiHospitalMode: (state, ward) => {
            state.isMultiHospitalMode = ward.payload;
        },
        setMapHospitalList: (state, ward) => {
            state.mapHospitalList = ward.payload;
        },
        setCurrentHospital: (state, ward) => {
            state.currentHospital = ward.payload;
        },
        setCleaningRequestState: (state, ward) => {
            state.cleaningRequestState = ward.payload;
        },
        setCleaningRequestObject: (state, ward) => {
            state.cleaningRequestObject = ward.payload;
        },
    },
});

export const wardsList = (state) => state.ward.wards;
export const statuses = (state) => state.ward.statuses;
export const loading = (state) => state.ward.loading;
export const loadingPatients = (state) => state.ward.loadingPatients;
export const loadingWards = (state) => state.ward.loadingWards;
export const wardPatients = (state) => state.ward.patients;
export const connectionError = (state) => state.ward.connectionError;
export const multiHospitalMode = (state) => state.ward.isMultiHospitalMode;
export const mapHospitalList = (state) => state.ward.mapHospitalList;
export const currentHospital = (state) => state.ward.currentHospital;
export const cleaningRequestState = (state) => state.ward.cleaningRequestState;
export const cleaningRequestObject = (state) => state.ward.cleaningRequestObject;

export const {
    setWards,
    setWardPatients,
    setBedasList,
    setBarsList,
    setSingleBedasSessions,
    setSingleBarsSessions,
    setLoading,
    setLoadingPatients,
    setLoadingWards,
    setUpdating,
    setStatuses,
    setConnectionError,
    setMultiHospitalMode,
    setCurrentHospital,
    setMapHospitalList,
    setCleaningRequestState,
    setCleaningRequestObject,
} = wardSlice.actions;

export const getBedasList =
    (wardId, isBars = false) =>
    (state) => {
        const hospitalId = state.ward.currentHospital;
        const combinedId = `${wardId}##${hospitalId}`;
        const key = isBars ? 'barsList' : 'bedasList';

        return state.ward?.statuses?.[combinedId]?.[key]?.[wardId] || state.ward?.[key]?.[wardId] || [];
    };

export const getSingleBedasSessions =
    (wardId, isBars = false) =>
    (state) => {
        const hospitalId = state.ward.currentHospital;
        const combinedId = `${wardId}##${hospitalId}`;
        const key = isBars ? 'singleBarsSessions' : 'singleBedasSessions';

        return state.ward?.statuses?.[combinedId]?.[key]?.[wardId] || state.ward?.[key]?.[wardId] || [];
    };

const updateDataOnServer = (dispatch, patientId, data, dataDate) => {
    dispatch(setUpdating(true));
    return new Promise(async function (resolve, reject) {
        API.updatePatientInWard(patientId, data, dataDate)
            .then((response) => {
                if (response.data === true) {
                    resolve(true);
                } else {
                    console.error('error in network', response);
                    resolve(false);
                }
            })
            .catch((err) => {
                console.error('error in network', err);
                resolve(false);
            })
            .finally(() => {
                dispatch(setUpdating(false));
            });
    });
};

export const loadWardList = () => async (dispatch, getState) => {
    if (getState().ward.loadingWards === false) {
        dispatch(setLoadingWards(true));
        API.getWardMapList(null)
            .then((response) => {
                if (response?.status === 200 && response?.data) {
                    const maps = _.filter(response?.data?.__data, (map) => !map.archived);
                    dispatch(setWards(maps || []));
                } else if (response?.status === 204) {
                    dispatch(setWards([]));
                } else {
                    dispatch(setWards([]));
                    console.error('ERROR Loading wards', response);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'There was a problem while downloading the wards list.',
                        }),
                    );
                }
            })
            .catch((err) => {
                dispatch(setWards([]));
                console.error('ERROR Loading wards', err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'There was a problem while downloading the wards list.',
                    }),
                );
            })
            .finally(() => {
                dispatch(setLoadingWards(false));
            });
    }
};

export const updatePatientList = (patientId, listId, boardId) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);

    for (let patient of patientsClone) {
        if (patient.__id === patientId) {
            const dataToUpdate = [];

            if (patient?.metadata?.listIds) {
                patient.metadata.listIds[boardId] = listId;
            } else {
                patient.metadata.listIds = {
                    [boardId]: listId,
                };
            }

            dataToUpdate.push({ ['metadata.listIds.' + boardId]: listId });

            // we need to update this data ...
            const status = await updateDataOnServer(dispatch, patient.__id, dataToUpdate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
                return true;
            } else {
                return false;
            }
        }
    }
};

/**
 * Adds a new bed history entry for a patient
 * @param {Array} dataToUpdate - Array to store updates that will be sent to server
 * @param {(Object|string|null)} bed - The bed to add to history, can be object with bed details, string bed ID, or null
 * @param {Object} patient - The patient object to update bed history for
 */
const addBedHistory = (dataToUpdate, bed, patient) => {
    const array = patient?.metadata?.bedHistory ? _.cloneDeep(patient?.metadata?.bedHistory) : [];
    if (_.isNull(bed)) {
        array.push({ bed: null, when: moment().toISOString() });
    } else if (_.isString(bed)) {
        array.push({ bed, when: moment().toISOString() });
    } else if (_.isObject(bed)) {
        array.push({ ...bed, when: moment().toISOString() });
    }
    _.set(patient, 'metadata.bedHistory', array);
    dataToUpdate.push({ 'metadata.bedHistory': array });
};

/**
 * Updates the bed assignment for a patient
 * @param {string} patientId - ID of the patient to update
 * @param {Object} body - Object containing bed assignment details
 * @param {string} body.bedId - ID of the bed to assign
 * @param {Object} body.bed - Bed object with name and details
 * @param {string} body.areaId - ID of the area containing the bed
 * @param {Object} body.area - Area object with name and details
 * @returns {Function} Redux thunk action that updates the patient's bed assignment
 * @async
 */
export const updatePatientBed = (patientId, body) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    const wards = getState().ward.wards;
    const hospitals = getState().ward.mapHospitalList;

    for (let patient of patientsClone) {
        if (patient?.__id === patientId) {
            let dataToUpdate, bed;
            const oldBed = patient?.bedDescription == null ? 'N/A' : patient?.bedDescription;
            const oldZone = patient?.areaDescription == null ? 'Patient Lobby' : patient?.areaDescription;
            const wardId = patient?.wardId;
            const hospitalId = patient?.facilityId;

            let backToLobby = false;

            if (body) {
                patient.bed = body.bedId;
                patient.bedDescription = body?.bed?.name;
                patient.area = body?.areaId;
                patient.areaDescription = body?.area?.name;

                dataToUpdate = [
                    { bed: body.bedId },
                    { bedDescription: body?.bed?.name },
                    { area: body?.areaId },
                    { areaDescription: body?.area?.name },
                ];

                bed = {
                    bed: body.bedId,
                    bedDescription: body?.bed?.name,
                    area: body?.areaId,
                    areaDescription: body?.area?.name,
                };
            } else {
                patient.bed = null;
                patient.bedDescription = null;
                patient.area = null;
                patient.areaDescription = null;
                backToLobby = true;

                dataToUpdate = [{ bed: null }, { bedDescription: null }, { area: null }, { areaDescription: null }];
                bed = null;
            }

            addBedHistory(dataToUpdate, bed, patient);

            const timeOfTransfer = moment();
            const status = await updateDataOnServer(dispatch, patient.__id, dataToUpdate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
                let newBedText = body?.bed?.name || body?.bedId;
                let newZoneText = body?.area?.name || body?.areaId;
                if (backToLobby) {
                    newBedText = 'N/A';
                    newZoneText = 'Patient Lobby';
                }
                const ward = _.find(wards, (w) => w?.wardsId?.[0] === wardId && w?.active);
                const hospital = _.find(hospitals, (h) => h?.__id === hospitalId);

                const locationBody = {
                    oldBed,
                    oldZone,
                    newBed: newBedText,
                    newZone: newZoneText,
                    ward,
                    hospital,
                    timeOfTransfer,
                };

                const note = Notes.getChangeOfPatientLocationNote(locationBody);
                API.addClinicalNote(patient?.patient?.__id, {
                    note: note,
                    patientsInWardsRef: [patient?.__id],
                    noteType: 'Location',
                    episodeId: patient?.admission?.consultantEpisodeId || null,
                })
                    .then(() => {
                        dispatch(setSnackbar({ type: 'success', message: 'Patient location note added.' }));
                    })
                    .catch((error) => {
                        console.error(error);
                        dispatch(setSnackbar({ type: 'error', message: 'Unable to save the clinical note.' }));
                    });
                return true;
            } else {
                return false;
            }
        }
    }
};

// TODO @Mateusz to update
export const updateBedasStatus =
    (hospitalId, data, afterSaveEvent, mode = 'create') =>
    async (dispatch, getState) => {
        const isDelete = mode === 'delete';
        const isUpdate = mode === 'update';

        const sliceKey = data?.isBars ? 'barsList' : 'bedasList';
        const bedasSliceState = getState().ward?.[sliceKey];

        let bedasClone = _.cloneDeep(getState().ward?.[sliceKey]?.[hospitalId] || []);

        if (isDelete) {
            _.remove(bedasClone, { sessionId: data.sessionId });
        } else if (isUpdate) {
            const index = _.findIndex(bedasClone, { sessionId: data.sessionId });
            if (index === -1) {
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Follow Up Session not saved. Session with given ID not found!',
                    }),
                );
                return;
            } else {
                bedasClone[index] = data;
            }
        } else {
            bedasClone.push(data);
        }

        if (!_.isEmpty(bedasClone)) {
            bedasClone = _.sortBy(bedasClone, ['startDate']);
        }

        const combinedState = {
            ...bedasSliceState,
            [hospitalId]: bedasClone,
            modifiedDate: moment().toISOString(),
        };

        // TODO @Mateusz. ED or seperated ids
        let ward = 'ED';

        try {
            const response = await API.updateWardData(ward, [{ [sliceKey]: combinedState }]);

            if (response.data) {
                const setter = data?.isBars ? setBarsList : setBedasList;
                dispatch(setSnackbar({ type: 'success', message: 'Follow Up Session was successfully saved.' }));
                dispatch(setter(combinedState));
            }
        } catch (e) {
            dispatch(setSnackbar({ type: 'error', message: 'Follow Up Session not saved. Error occurred!' }));
            console.error('error in network', e);
        } finally {
            if (afterSaveEvent) {
                afterSaveEvent();
            }
        }
    };

// TODO @Mateusz to update
export const updateSingleBedasSessions = (hospitalId, data, afterSaveEvent) => async (dispatch, getState) => {
    const sliceKey = data?.isBars ? 'singleBarsSessions' : 'singleBedasSessions';

    let sessionsClone = _.cloneDeep(data);
    let sessionsFromSlice = _.cloneDeep(getState().ward?.[sliceKey]?.[hospitalId] || []);

    // Look if there is any session in a data array and iterate through our slice state,
    // get this item and remove because this object in a data object will overwrite current state with newer state
    if (!_.isEmpty(sessionsFromSlice)) {
        _.forEach(data, (sess) => {
            const sliceIndex = sessionsFromSlice.findIndex((item) => item?.from === sess?.from && item?.startDate === sess?.startDate);
            if (sliceIndex >= 0) {
                sessionsFromSlice.splice(sliceIndex, 1);
            }
        });
    }

    sessionsClone = [...sessionsClone, ...sessionsFromSlice];

    // Sort events by start date and remove older than today
    if (!_.isEmpty(sessionsClone)) {
        sessionsClone = _.sortBy(sessionsClone, ['from']);
        sessionsClone = _.filter(sessionsClone, (session) =>
            moment(session?.from || session?.startDate).isSameOrAfter(
                moment().set({
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                    milliseconds: 0,
                }),
            ),
        );
    }

    const combinedSessions = {
        ...getState().ward?.[sliceKey],
        [hospitalId]: sessionsClone,
        modifiedDate: moment().toISOString(),
    };

    // TODO @Mateusz. ED or seperated ids
    let ward = 'ED';

    try {
        const response = await API.updateWardData(ward, [{ [sliceKey]: combinedSessions }]);
        if (response.data) {
            const setter = data?.isBars ? setSingleBarsSessions : setSingleBedasSessions;
            dispatch(setSnackbar({ type: 'success', message: 'Follow Up Session was successfully saved.' }));
            dispatch(setter(combinedSessions));
        }
    } catch (e) {
        dispatch(setSnackbar({ type: 'error', message: 'Follow Up Session not saved. Error occurred!' }));
        console.error('error in network', e);
    } finally {
        if (afterSaveEvent) {
            afterSaveEvent();
        }
    }
};

export const getSingleSessionsByDay = (day, hospitalId, isBars = false) => {
    const sliceKey = isBars ? 'singleBarsSessions' : 'singleBedasSessions';
    return (dispatch, getState) => {
        const sessions = getState().ward?.[sliceKey]?.[hospitalId];
        return _.filter(sessions, (session) => {
            return moment(session.from || session?.startDate).isSame(day, 'day');
        });
    };
};

export const updatePatientBedStatus = (bed, status) => async (dispatch, getState) => {
    let statusesDataClone = _.cloneDeep(getState().ward.statuses);
    let response;
    try {
        let wardId = bed?.wardId;
        const bedId = bed?.__id;

        if (statusesDataClone[wardId]?.bedStatuses == null) {
            statusesDataClone[wardId].bedStatuses = {};
        }

        if (status === 'unlock') {
            delete statusesDataClone[wardId].bedStatuses[bedId];
        } else {
            statusesDataClone[wardId].bedStatuses[bedId] = status;
        }

        response = await API.updateWardData(wardId, [{ bedStatuses: statusesDataClone[wardId].bedStatuses }]);
    } catch (e) {
        console.error(e);
        dispatch(setSnackbar({ type: 'error', message: 'Unable to save bed status.' }));
    }

    if (response.data) {
        dispatch(setStatuses(statusesDataClone));
        dispatch(setSnackbar({ type: 'success', message: 'Bed status updated successfully.' }));
    } else {
        dispatch(setSnackbar({ type: 'error', message: 'Unable to save bed status.' }));
        console.error('error in network', response);
    }
};

export const updatePatientStatuses = (icon, patient, type) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    for (let dataPatient of patientsClone) {
        if (dataPatient.__id === patient.__id) {
            if (!dataPatient.flags) {
                dataPatient.flags = [];
            }
            let founded = false;
            for (let f of dataPatient.flags) {
                if (f.name === icon.name) {
                    founded = true;
                    if (f.value === 1) {
                        if (dispatch(hasPermission(Permissions.AGYLE_CAN_REMOVE_TASKS))) {
                            f.value--;
                        } else {
                            dispatch(
                                setSnackbar({
                                    type: 'warning',
                                    message: 'You do not have permission to delete this task.',
                                }),
                            );
                        }
                    } else {
                        f.value--;
                    }
                }
            }
            if (!founded) {
                let obj = {
                    name: icon.name,
                    value: icon.statusesCount - 1,
                };
                if (type !== 'task') obj['type'] = type;
                dataPatient.flags.push(obj);
            }
            _.remove(dataPatient.flags, (v) => v.value <= 0);

            const status = await updateDataOnServer(dispatch, dataPatient.__id, [{ flags: dataPatient.flags }]);
            // const status = true;
            if (status) {
                dispatch(setWardPatients(patientsClone));
            }
        }
    }
};

export const updatePatientsData = (updates, afterSaveEvent) => async (dispatch, getState) => {
    API.updatePatientsInWard(updates)
        .then((response) => {
            // set patients data in parent records!
            if (response.data === true) {
                afterSaveEvent(true);

                const patientsClone = _.cloneDeep(getState().ward.patients);
                for (let patientCloned of patientsClone) {
                    for (let update of updates) {
                        if (update.patientInWardId === patientCloned.__id) {
                            // apply all updates
                            _.forEach(update.set, (s) => {
                                _.forEach(s, (s, name) => {
                                    _.set(patientCloned, name, s);
                                });
                            });
                        }
                    }
                }

                dispatch(setWardPatients(patientsClone));
            } else {
                afterSaveEvent(false);
            }
        })
        .catch((error) => {
            console.error(error);
            dispatch(setSnackbar({ type: 'error', message: 'Failed to save data. Please try again.' }));
        });
};

export const isInCurrentWard = (patientId) => async (dispatch, getState) => {
    const patients = getState().ward?.patients;
    if (patients) {
        for (const patient of patients) {
            if (patient.__id === patientId) {
                return true;
            }
        }
    }

    return false;
};

export const updatePatientData = (patient, data, afterSaveEvent, dataDate) => async (dispatch, getState) => {
    const patientsClone = _.cloneDeep(getState().ward.patients);
    for (let patientCloned of patientsClone) {
        if (patientCloned.__id === patient.__id) {
            _.forEach(data, (d) => {
                _.forEach(d, (d, n) => {
                    _.set(patientCloned, n, d);
                });
            });

            const status = await updateDataOnServer(dispatch, patient.__id, data, dataDate);
            if (status) {
                dispatch(setWardPatients(patientsClone));
            }
            if (afterSaveEvent) {
                afterSaveEvent(status);
            }
            break;
        }
    }
};

/**
 * Updates ward patients data by ward ID
 * @param {string[]} wardIdArray - Array of ward IDs to get updates for
 * @param {AbortSignal} signal - AbortController signal for canceling requests
 * @returns {Function} Redux thunk action that updates ward patients data
 */
export const updateWardPatientsByWardId = (wardIdArray, signal) => (dispatch, getState) => {
    if (getState().ward.loading === undefined && !getState().ward.updating && !getState().ward.loadingPatients) {
        dispatch(setLoading(wardIdArray));

        const patientsInfo = [];
        for (let patientInWard of getState().ward.patients) {
            if (patientInWard?.__id) {
                // form remove null as piw id
                patientsInfo.push([patientInWard.__id, patientInWard.objectVersion]);
            }
        }

        dispatch(setUpdating(true));
        API.getWardsAttendancesUpdates(wardIdArray, patientsInfo, signal)
            .then((changes) => {
                const patientsClone = getState().ward.patients ? _.cloneDeep(getState().ward.patients) : [];
                let updatedStatus = false;
                if (!_.isEmpty(changes?.deleted)) {
                    updatedStatus = true;
                    _.remove(patientsClone, (pat) => {
                        let patDeleted = false;
                        for (let deletedId of changes.deleted) {
                            if (deletedId === pat.__id) {
                                patDeleted = true;
                                break;
                            }
                        }
                        return patDeleted;
                    });
                }
                if (!_.isEmpty(changes?.added)) {
                    updatedStatus = true;
                    for (let patientToAdd of changes.added) {
                        patientsClone.push(patientToAdd);
                    }
                }
                if (!_.isEmpty(changes?.updated)) {
                    updatedStatus = true;
                    for (let patientUpdate of changes.updated) {
                        _.forEach(patientsClone, (pat) => {
                            if (pat.__id === patientUpdate.__id) {
                                // _.merge(pat, patientUpdate);
                                _.assign(pat, patientUpdate);
                            }
                        });
                    }
                }
                if (updatedStatus) {
                    dispatch(setWardPatients(patientsClone));
                }

                let multiWards = {};
                let areSameStatuses = true;
                const oldStatuses = _.cloneDeep(getState().ward.statuses);

                _.forEach(changes.statuses, (stats) => {
                    multiWards = {
                        ...multiWards,
                        [stats.wardId]: stats,
                        modifiedDate: moment().toISOString(),
                    };

                    if (areSameStatuses)
                        areSameStatuses =
                            _.isEqual(oldStatuses?.[stats.wardId]?.bedStatuses, stats?.bedStatuses) &&
                            _.isEqual(oldStatuses?.[stats.wardId]?.bedasList, stats?.bedasList) &&
                            _.isEqual(oldStatuses?.[stats.wardId]?.barsList, stats?.barsList) &&
                            _.isEqual(oldStatuses?.[stats.wardId]?.singleBedasSessions, stats?.singleBedasSessions) &&
                            _.isEqual(oldStatuses?.[stats.wardId]?.singleBarsSessions, stats?.singleBarsSessions);
                });

                if (!areSameStatuses) {
                    dispatch(setStatuses(multiWards));
                }

                dispatch(setConnectionError(false));
            })
            .catch((error) => {
                console.error(error);
                dispatch(setConnectionError(true));
            })
            .finally(() => {
                dispatch(setLoading(undefined));
                dispatch(setUpdating(false));
            });
    }
};

/**
 * Loads patients and ward data for specified ward IDs
 * @param {string[]} wardIdArray - Array of ward IDs to load data for
 * @returns {Function} Thunk action that loads ward patients and data
 * @async
 */
export const loadWardPatientsByWardId = (wardIdArray) => async (dispatch, getState) => {
    if (getState().ward.loading !== wardIdArray) {
        dispatch(setWardPatients([]));
        dispatch(setLoading(wardIdArray));
        dispatch(setLoadingPatients(true));
        API.getWardsAttendances(wardIdArray)
            .then((r) => {
                const ptArr = [];
                _.forEach(r, (pt) => {
                    if (pt) {
                        let elementHidden = false;
                        if (_.startsWith(pt?.bed, 'wlist') || !pt?.bed) {
                            if (pt?.admission?.locationDescription === 'DISCHARGE') {
                                elementHidden = true;
                            }
                        }

                        if (!elementHidden) ptArr.push(pt);
                    }
                });
                dispatch(setWardPatients(ptArr));
            })
            .catch((error) => {
                console.error(error);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Unable to load patients data. Please try again later.',
                    }),
                );
            })
            .finally(() => dispatch(setLoadingPatients(false)));

        let id = wardIdArray[0];
        await API.getWardData(id).then((stat) => {
            dispatch(
                setStatuses({
                    [stat.wardId]: stat,
                }),
            );

            if (stat?.bedasList) {
                dispatch(setBedasList(stat?.bedasList));
            }
            if (stat?.singleBedasSessions) {
                dispatch(setSingleBedasSessions(stat?.singleBedasSessions));
            }
            if (stat?.barsList) {
                dispatch(setBarsList(stat?.barsList));
            }
            if (stat?.singleBarsSessions) {
                dispatch(setSingleBarsSessions(stat?.singleBarsSessions));
            }
        });
        dispatch(setLoading(undefined));
    }
};

export const saveCleaningRequest = (data) => async (dispatch, getState) => {
    if (!data?.wardId || !data?.cleaningRequest) {
        console.error('Wrong data structure.');
        return null;
    }
    const userObject = getState().user.userData;
    const userFullName = Formatter.formatUserFullName(userObject);

    return await API.saveCleaningRequest(data)
        .then((res) => {
            const auditData = {
                cleaningId: data.cleaningRequest.__id,
                user: userFullName,
                login: userObject?.userName,
                cleaningRequest: data.cleaningRequest,
            };

            dispatch(
                setSnackbar({
                    type: 'success',
                    message: 'Cleaning request has been saved successfully.',
                }),
            );

            dispatch(sendUserAudit(null, AUDIT_TYPES.CLEANING_REQUEST, auditData));

            return res;
        })
        .catch((error) => {
            console.error(error);
            dispatch(
                setSnackbar({
                    type: 'error',
                    message: 'Unable to save the cleaning bed request. Please try again later.',
                }),
            );
        });
};

/**
 * Bed cleaning request function
 *
 * If a map has a custom cleaning option, it will be used, otherwise the global settings will be used.
 *
 * @param {string} bed - Bed id form DB
 * @param {string} type - Type of action
 */
export const cleaningRequestPath = (bed, type) => async (dispatch, getState) => {
    const cleaningRequestEnabled = getConfig('modules.cleaningRequest.enabled', false);
    if (cleaningRequestEnabled && bed && bed?.length > 10) {
        // Length check for ObjectId (new map bed)
        const conf = getState().externalConfig.cleaningRequestCfg;
        const customMapOpt = getState().wardMap?.customCleaningMapOption;
        // Get a bed object
        const bedObj = await API.getBed(bed);
        // If the bed exists
        if (bedObj) {
            if (customMapOpt) {
                // Check that the cleaning option is enabled for this type of bed
                if (bedObj?.forCleaning) {
                    const getModeType = customMapOpt?.[type] || 'manual';
                    switch (getModeType) {
                        case 'auto':
                            dispatch(autoCleaningRequest(bedObj));
                            break;
                        case 'prompt':
                            dispatch(setCleaningRequestState(bedObj));
                            break;
                        case 'manual':
                        default:
                            return;
                    }
                }
            } else if (conf?.triggers?.[type]) {
                // Check if cleaning is enabled for the specific type
                // Check if the bed is not a list
                if (bedObj?.type !== 'wlist') {
                    const isED = bedObj?.wardId === 'ED';
                    const location = conf?.location?.[isED ? 'ED' : 'IP'];
                    // Check if cleaning is enabled for the specific ward type
                    if (location?.enabled) {
                        if (location?.auto) {
                            // Submit an automated request
                            dispatch(autoCleaningRequest(bedObj));
                        } else {
                            // Show request popup
                            dispatch(setCleaningRequestState(bedObj));
                        }
                    }
                }
            }
        } else {
            console.error(`For bed id [${bed}] cannot load object from DB.`);
            dispatch(
                setSnackbar({
                    type: 'error',
                    message: 'Unable to save the cleaning bed request. Please try again later.',
                }),
            );
        }
    }
};

/**
 * Function to automatically request bed cleaning
 * @param {Object} bed - Bed object
 */
const autoCleaningRequest = (bed) => (dispatch, getState) => {
    const conf = getState().externalConfig.cleaningRequestCfg;
    const findManagementType = _.find(conf?.wards, (w) => w.hospitalId === bed?.hospitalId && w.wardId === bed?.wardId);
    const getEstimatedTime = conf?.levels?.level1;
    const wardIds = dispatch(getWardsIdForPatientsSearch([bed?.wardId]));

    const userObject = getState().user.userData;
    const userFullName = Formatter.formatUserFullName(userObject);

    let data = {
        wardId: wardIds[0],
        cleaningRequest: {
            bedId: bed?.__id,
            level: '1',
            description: 'Auto Request',
            status: 'OPEN',
            managementType: findManagementType?.option || 'internal',
            wardId: bed?.wardId,
            hospitalId: bed?.hospitalId,
            expectedDate: moment()
                .add(getEstimatedTime || 0, 'minute')
                .toISOString(),
            createdByUserName: userFullName,
        },
    };
    dispatch(saveCleaningRequest(data));
};

export default wardSlice.reducer;
